import { Component, OnDestroy, OnInit } from "@angular/core";
import { DataService } from "../../../_services/data.service";
import { AuthenticationService } from "../../../_services/authentication.service";
import { BehaviorSubject } from "rxjs/BehaviorSubject";

import { Router } from "@angular/router";
import { NgbModal, NgbModalRef } from "@ng-bootstrap/ng-bootstrap";
import { sleep } from "../../../_helpers/sleep";
import { Observable } from "rxjs/Observable";
import { ITranslations } from "../../../_models/ITranslations";
import { TranslationService } from "../../../_services/translation.service";
import { SessionManagerService } from "../../../_services/SessionManager/session-manager.service";
import { UsersTableSearchState } from "../../../_services/user-management.service";
import { GeneralRowEvent } from "../../../_models/general-row-event";

import { PopoverService } from "../../../_services/popover.service";
import { ApprovalModalComponent } from "../../../common/approval-modal/approval-modal.component";
import { PasswordResetService } from "../../../_services/password-reset.service";
import { BaseDiscountModel } from "../discount-models/BaseDiscountModel";
import { baseDiscountMocks } from "../mock/baseDiscount.mock";
import { DiscountManagementFormMainModalContainerComponent } from "../discount-management-form-main-modal-container/discount-management-form-main-modal-container.component";
import { DiscountManagementService } from "../../../_services/discount-management.service";
import { GenericModalComponent } from "../../../form/generic-app-modal/generic-modal.component";
import { DevToolsService } from "../../../_services/dev-tools.service";

@Component({
  selector: "discount-management-page",
  templateUrl: "./discount-management-page.component.html",
  styleUrls: [
    "./discount-management-page.component.scss",
    "../../events-screen/parent-events-page/parent-events-page.component.scss",
  ],
})
export class DiscountManagementPageComponent implements OnInit, OnDestroy {
  public selectedDiscountId: string | null = null;
  public translations$: Observable<ITranslations>;

  public readonly $discountTable: BehaviorSubject<BaseDiscountModel[]> =
    new BehaviorSubject<BaseDiscountModel[]>(baseDiscountMocks);
  public readonly $discountSearchAbstract: BehaviorSubject<{
    numberOfDiscount: number;
    totalSum: string;
  }> = new BehaviorSubject<{
    numberOfDiscount: number;
    totalSum: string;
  }>(null);

  public readonly devBaseDiscountMocks: BaseDiscountModel[] = baseDiscountMocks;
  public readonly $openUserView: BehaviorSubject<string> =
    new BehaviorSubject<string>("");
  public readonly $usersTableSearchState: BehaviorSubject<UsersTableSearchState> =
    new BehaviorSubject<UsersTableSearchState>(null);
  public readonly $openDeleteModal: BehaviorSubject<BaseDiscountModel | false> =
    new BehaviorSubject<BaseDiscountModel | false>(false);
  public scrollCallback: ($event: any) => void;

  constructor(
    protected discountManagementService: DiscountManagementService,
    protected devToolsService: DevToolsService,
    protected modalService: NgbModal,
    protected router: Router,
    protected dataService: DataService,
    protected authenticationService: AuthenticationService,
    protected translationService: TranslationService,
    protected popoverService: PopoverService,
    protected passwordResetService: PasswordResetService,
    protected sessionManagerService: SessionManagerService
  ) {
    this.getUsers();
    this.translations$ = this.translationService.translations;
    this.scrollCallback = this.scrollCallbackHandler.bind(this);
  }

  public get isDevelopmentMode() {
    return this.sessionManagerService.isDevModeActive();
  }

  public toastMessage(message: string) {
    const options = {
      positionClass: "toast-top-center",
      maxShown: 2,
      newestOnTop: true,
      animate: true,
      toastLife: 10,
      enableHTML: true,
      dismiss: true,
      messageClass: "toast-message",
      titleClass: "toast-title",
      showCloseButton: true,
    };
    this.popoverService.popMessage(message);
  }

  public ngOnInit(): void {}

  public ngOnDestroy(): void {}

  public async rowEventHandler($event: GeneralRowEvent<BaseDiscountModel>) {
    const discountId = $event.eventId;

    switch ($event.action) {
      case "select":
        this.selectedDiscountId = discountId;

        break;
      case "unselect":
        this.selectedDiscountId = null;
        break;
      case "open":
        this.$openUserView.next(discountId);
        break;
      case "close":
        this.$openUserView.next("");
        break;
      case "delete":
        this.$openDeleteModal.next($event.eventObject);
        break;
      case "edit":
        this.editDiscountFromSearchResults($event.eventObject);
        break;
    }
  }

  public editDiscountFromSearchResults(discount: BaseDiscountModel) {
    const modalRef: NgbModalRef = this.modalService.open(
      DiscountManagementFormMainModalContainerComponent,
      DiscountManagementFormMainModalContainerComponent.modalOptions
    );
    (
      modalRef.componentInstance as DiscountManagementFormMainModalContainerComponent
    ).modalEventIdInput = String(discount.discountId);

    (
      modalRef.componentInstance as DiscountManagementFormMainModalContainerComponent
    ).discountToEdit = discount;

    (
      modalRef.componentInstance as DiscountManagementFormMainModalContainerComponent
    ).customEventType = "editDiscount";
  }

  public deleteModalResultHandler($event: any) {
    this.deleteDiscount($event);
  }

  public scrollCallbackHandler($event: any) {
    this.discountManagementService.scrolledToBottomOfResults().then();
  }

  public addNewDiscount() {
    const modalRef: NgbModalRef = this.modalService.open(
      DiscountManagementFormMainModalContainerComponent,
      DiscountManagementFormMainModalContainerComponent.modalOptions
    );
    (
      modalRef.componentInstance as DiscountManagementFormMainModalContainerComponent
    ).modalEventIdInput = "new";
    (
      modalRef.componentInstance as DiscountManagementFormMainModalContainerComponent
    ).customEventType = "discount";
  }

  public showFeatureNotImplementedModal() {
    const modalRef: NgbModalRef = this.modalService.open(
      ApprovalModalComponent
    );
    modalRef.componentInstance.info = {
      mainMessage:
        this.translationService.currentTranslations
          .feature_not_implemented_text,
      approveText:
        this.translationService.currentTranslations
          .payment_return_action_type_APPROVE,
      modalHeader:
        this.translationService.currentTranslations
          .feature_not_implemented_header,
      hideCancelButton: true,
    };
  }

  public handleSearchSectionEvent($event: GeneralRowEvent<BaseDiscountModel>) {
    this.editDiscountFromSearchResults($event.eventObject);
  }

  public exportToExcel() {
    this.discountManagementService.exportToExcel();
    return;
  }

  protected modalNotification(message: string) {
    let modalText = message.toString();

    const modalRef: NgbModalRef = this.modalService.open(GenericModalComponent);
    modalRef.componentInstance.modalOptions = {
      approveOption: "אישור",
      cancelOption: "",
      modalHeader: "סבסודים",
      modalText,
      onlyApprove: true,
    };
    modalRef.result.then((res) => {});
  }

  private getUsers(): void {
    this.discountManagementService
      .getAllDiscountsInTable()
      .subscribe((discounts: BaseDiscountModel[]) => {
        this.$discountTable.next(discounts);
      });
    this.discountManagementService
      .getDiscountSearchResultsAbstract()
      .subscribe((results) => {
        this.$discountSearchAbstract.next(results);
      });
  }

  private async deleteDiscount(eventObject: BaseDiscountModel) {
    const result = await this.discountManagementService.deleteDiscount(
      eventObject.discountId
    );
    if (result.success) {
      this.modalNotification(
        `הסבסוד ל${(eventObject.childFirstName || "").trim()} ${
          eventObject.childLastName
        } נמחק בהצלחה`
      );
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/admin/discounts/discount-management-page/discount-management-page.component.ts